import ButtonChat from "@/components/shared/futureChat/buttons/ButtonChat";
import Card from "@/components/shared/futureChat/cards/Card";
import ArrowRight from "@/components/shared/icons/ArrowRight";
import IconCancel from "@/components/shared/icons/Cancel";
import ExportedImage from "next-image-export-optimizer";
import store from "stores/store";
import { isOnline } from "@/constant/env";
import { getAltTextFromGeneralImage, getImageUrlFromGeneralImage } from "utils/imageHelpers";
import { cn } from "@/lib/utils";
import { useContext, useEffect } from "react";
import { TrackingContext } from "@/components/shared/tracking/Tracking";
import { ImageProcessingContext } from "@/components/shared/imageProcessing/ImageProcessing";
import { ChatbotUiState } from "@/components/enums/UiState";
import Markdown from "react-markdown";

type CancelCallProps = {
    onGoBack: () => void;
};

const OpenLink: React.FC<CancelCallProps> = ({ onGoBack }) => {
    if (!store) {
        throw Error("Component was not setup correctly");
    }

    const {
        cancelCallLocalized,
        endOfCallRegularLocalized,
        globalLocalized,
        chatbotLocalized,
        updateDirectLinkFromChatbot,
        updateRenderChatbotContent,
    } = store;

    const link_title = chatbotLocalized?.cancelCallLinkTitle ?? "Neue Leben";
    const link_url = chatbotLocalized?.cancelCallLinkUrl ?? "TriffDeineZukunft.de";

    const { trackView, trackEndChat, screenView } = useContext(TrackingContext);

    useEffect(() => {
        screenView("4.4 - Open Link");
    }, []);

    const { cancelProcessing } = useContext(ImageProcessingContext);

    return (
        <div className="flex h-full w-full flex-col items-center backdrop-blur-[10px]">
            <div className="absolute bottom-[60px] flex max-w-[768px] flex-col gap-[14px] px-[32px]">
                <Card className="gap-[7px]">
                    <div className="font-frutigerBold text-[14px] uppercase italic leading-[135%] tracking-minus-2">
                        {chatbotLocalized?.linkPageCardTitle}
                    </div>
                    <Markdown className="font-frutigerBold text-[10px] leading-[135%] tracking-minus-2">
                        {chatbotLocalized?.linkPageCardDescription ?? ""}
                    </Markdown>

                    <a
                        target="blank"
                        title={link_title}
                        href={`https://${link_url}`}
                        className={cn(
                            "block select-none overflow-hidden font-frutigerBold leading-[16px]",
                            "4k:rounded-[30px] rounded-[7px] border-[1px] border-whiteShade-30 shadow-[0_0_13px_0_rgba(255,255,255,0.3)]",
                        )}
                    >
                        <div className={cn({ "shadow-[inset_0_0_26px_6.6px_rgba(255,255,255,0.6)]": isOnline })}>
                            <div
                                className={cn("4k:gap-16 flex gap-2", {
                                    "4k:p-12 p-3 shadow-[inset_0_0.66px_5pxpx_0_rgba(255,255,255,0.35)]": isOnline,
                                })}
                            >
                                <ExportedImage
                                    src={
                                        "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAABBCAMAAABW61JJAAAATlBMVEVMaXH////Z2dnw8PDZ2dnZ2dndCy/X19fa2trZ2dn49PXx8fHgHT7zqLTmSmX1ucPiME/++vvpXnbviJn4zdT73+PeETTxmqnsc4jh4eE3r8e1AAAACnRSTlMA////Z5//LO227rX1uwAAAAlwSFlzAAAWJQAAFiUBSVIk8AAAAedJREFUeJztl9lygzAMRW1IDHHxvsH//2gLDSk2EgnQh3Ym9zEzPsjSlRURcl7Nta0Oqb024/lLNdQHVVUXQpqqpodVVw25DsfPUzrcSHsiAErrllQfpwDVG1D/jxzwoFISnpsjAK563bFJUdvEdwKM0yxTtGEHwKT7t3MEfxXAJQPVidcAvoh+EUT/CkAB4T/UPwf4uHGesd48AfApfm2d8ir1wGXSE4BkjEkx59t4u8pk2ASoZZSTRJkSuQXgmkVV/BbKe6gNQPq544LQQSHAAL2oU36vpTwKCKyDGo/2gBlAQAIuMIFzb2iDAexco1J5c8SAALi28PkxuWUdIECIDgH4tRvBCMSUX0BcZPJ/6VUWXSY0B51CAKpwqcGq4BEAL3yOZFFGg+VAAi5dKcR7DwMAlwNgK1rmUIAvOg3yomDR42WUBWH9rdDNrxAIEAWAObMeE2rLSLIkZCPMiMiYplsAXwJY5+ZUciGxzC6sXBRiVJROKJHst0tAfy4AZvX2Py9M3kwcnaYb9s66cSuG/F8BAqCmx0ZqPqZQAKUKvIYGDIAAKHer/wXaIeGDAHov+qwoFd//pAXlrJRSWveY8PsAO/QG/E4O2rNr3+3s4nl69Z2Wb35IH8O4fBPS3I6u/7ev9f8TYUgzjCPxyIYAAAAASUVORK5CYII="
                                    }
                                    alt={link_title}
                                    fill={true}
                                    className={cn(
                                        "4k:h-[260px] 4k:w-[260px] 4k:rounded-[32px] relative h-8 w-8",
                                        "shadow-[0_0_15px_0_rgba(0,0,0,0.4)]",
                                    )}
                                />
                                <div className="flex flex-col justify-center">
                                    <div className="text-[12px] font-bold">{link_title}</div>
                                    <div className="4k:text-[38px] text-[10px]">{link_url}</div>
                                </div>
                                <div className="ml-auto flex items-center pl-2">
                                    <svg width="18" height="19" viewBox="0 0 18 19" fill="none">
                                        <rect x="0.5" y="0.838867" width="17" height="17" rx="8.5" stroke="white" />
                                        <path
                                            d="M5.5 9.33887H12.5M12.5 9.33887L9 5.83887M12.5 9.33887L9 12.8389"
                                            stroke="white"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </a>
                </Card>
                <div className="flex flex-col gap-[12px]">
                    <ButtonChat
                        iconRight={
                            <ExportedImage
                                src={
                                    chatbotLocalized?.linkPageButtonOne.icon !== undefined &&
                                    chatbotLocalized?.linkPageButtonOne.icon !== null
                                        ? getImageUrlFromGeneralImage(chatbotLocalized?.linkPageButtonOne.icon)
                                        : ""
                                }
                                alt={
                                    chatbotLocalized?.linkPageButtonOne.icon !== undefined &&
                                    chatbotLocalized?.linkPageButtonOne.icon !== null
                                        ? getAltTextFromGeneralImage(chatbotLocalized?.linkPageButtonOne.icon)
                                        : ""
                                }
                                layout="fill"
                                objectFit="cover"
                                objectPosition="center"
                            />
                        }
                        onClick={() => {
                            updateDirectLinkFromChatbot(true);
                            //window.open(`https://${link_url}`);
                            window.open(`https://${link_url}`);
                            //updateRenderChatbotContent(ChatbotUiState.LANDING);
                        }}
                    >
                        {chatbotLocalized?.linkPageButtonOne.text}
                    </ButtonChat>
                    <ButtonChat
                        variant="secondary"
                        iconRight={
                            <ExportedImage
                                src={
                                    chatbotLocalized?.linkPageButtonOne.icon !== undefined &&
                                    chatbotLocalized?.linkPageButtonTwo.icon !== null
                                        ? getImageUrlFromGeneralImage(chatbotLocalized?.linkPageButtonTwo.icon)
                                        : ""
                                }
                                alt={
                                    chatbotLocalized?.linkPageButtonTwo.icon !== undefined &&
                                    chatbotLocalized?.linkPageButtonTwo.icon !== null
                                        ? getAltTextFromGeneralImage(chatbotLocalized?.linkPageButtonTwo.icon)
                                        : ""
                                }
                                layout="fill"
                                objectFit="cover"
                                objectPosition="center"
                            />
                        }
                        onClick={() => {
                            trackView("backtochat");
                            onGoBack();
                        }}
                    >
                        {chatbotLocalized?.linkPageButtonTwo.text}
                    </ButtonChat>
                </div>
            </div>
        </div>
    );
};

export default OpenLink;
